/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { useRef, useMemo } from 'preact/hooks';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { classNames } from '../utils/UNSAFE_classNames';
import { TabbableModeContext } from '../hooks/UNSAFE_useTabbableMode';
import { SelectionMode, CurrentItemVariant } from '../UNSAFE_Collection';
import { FocusTrap } from '../UNSAFE_FocusTrap';
import { useId } from '../hooks/UNSAFE_useId';
import { Roles } from '../UNSAFE_ListView';
import { getListViewRoles } from './ListRoles';

// default style class for items in ListView
export const ITEM_STYLE_CLASS = 'oj-listview-item';
export const ITEM_SELECTOR = '.' + ITEM_STYLE_CLASS;

const styles = {
  base: cssProps`${{
    display: 'flex',
    alignItems: 'center',
    minHeight: 'var(--oj-collection-list-row-height)',
    boxSizing: 'border-box',
    backgroundImage:
      'linear-gradient(var(--oj-c-PRIVATE-DO-NOT-USE-core-divider-color),var(--oj-c-PRIVATE-DO-NOT-USE-core-divider-color))',
    backgroundSize: 0,
    backgroundPosition: '0 100%',
    backgroundRepeat: 'no-repeat',
    borderTop: '1px solid transparent'
  }}`,
  gridlines: cssProps`${{
    backgroundSize: '100% 1px'
  }}`,
  gridlineTop: cssProps`${{
    backgroundImage: `linear-gradient(var(--oj-c-PRIVATE-DO-NOT-USE-core-divider-color),var(--oj-c-PRIVATE-DO-NOT-USE-core-divider-color)),
      linear-gradient(var(--oj-c-PRIVATE-DO-NOT-USE-core-divider-color),var(--oj-c-PRIVATE-DO-NOT-USE-core-divider-color))`,
    backgroundPosition: '0 100%, 0 -1px',
    backgroundSize: '100% 1px, 100% 1px'
  }}`,
  selectable: cssProps`${{
    '&:hover': {
      backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-bg-color-hover);'
    }
  }}`,
  selected: cssProps`${{
    backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-bg-color-selected)'
  }}`,
  selectedSingle: cssProps`${{
    borderTop: '1px solid var(--oj-c-PRIVATE-DO-NOT-USE-core-border-color-selected)',
    boxShadow: 'inset 0 -1px var(--oj-c-PRIVATE-DO-NOT-USE-core-border-color-selected)'
  }}`,
  suggestionEnd: cssProps`${{
    backgroundSize: '100% 1px'
  }}`,
  highlight: cssProps`${{
    backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-bg-color-hover);'
  }}`,
  focused: cssProps`${{
    outlineStyle: 'dotted',
    outlineWidth: '1px',
    outlineColor: 'var(---oj-c-PRIVATE-DO-NOT-USE-core-focus-border-color)',
    outlineOffset: '-1px'
  }}`
};

const cellStyles = {
  base: cssProps`${{
    flexGrow: '1'
  }}`,
  checkboxContainer: cssProps`${{
    display: 'flex'
  }}`
};

/**
 * Props for the ListViewItem Component
 */
export type Props<K> = {
  children: ComponentChildren;
  itemKey: K;
  itemIndex: number;
  isFocused: boolean;
  isFocusRingVisible: boolean;
  isGridlineVisible: boolean;
  isSelected: boolean;
  isTopGridlineVisible?: boolean;
  isTabbable?: boolean;
  role: Roles;
  selectionMode: SelectionMode;
  suggestion?: 'end' | true;
  currentItemVariant?: CurrentItemVariant;
};

/**
 * The internal component used to render a single item in ListView.
 */
export function ListViewItem<K extends string | number>({
  children,
  itemKey,
  itemIndex,
  isFocused,
  isFocusRingVisible,
  isGridlineVisible,
  isSelected,
  isTopGridlineVisible = false,
  isTabbable = false,
  role,
  selectionMode,
  suggestion,
  currentItemVariant
}: Props<K>) {
  const rootRef = useRef<HTMLDivElement>(null);

  const roles = useMemo(() => getListViewRoles(role), [role]);

  // some roles come with related aria attributes which need to be set
  const cellRoleAttributes =
    roles.cell === 'option'
      ? {
          role: roles.cell,
          'aria-posinset': itemIndex + 1,
          'aria-setsize': -1
        }
      : {
          role: roles.cell
        };

  // don't bubble Arrow Up and Down events to parent when in isTabbable mode
  // this bubbling causes the outer CollectionFocus ring to change index while in the isTabbable mode.
  const handleKeyDown = (event: KeyboardEvent) => {
    if (isTabbable && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.stopPropagation();
    }
  };

  const classes = classNames([
    styles.base,
    ITEM_STYLE_CLASS,
    isFocused && isFocusRingVisible && styles.focused,
    isGridlineVisible && styles.gridlines,
    isTopGridlineVisible && styles.gridlineTop,
    isSelectable(selectionMode, isSelected) && !isSelected && styles.selectable,
    isSelected && styles.selected,
    isSelected && selectionMode === 'single' && styles.selectedSingle,
    suggestion === 'end' &&
      !isGridlineVisible &&
      !(isSelected && selectionMode === 'single') &&
      styles.suggestionEnd,
    currentItemVariant === 'highlight' && isFocused && !isSelected && styles.highlight
  ]);

  const cellClasses = classNames([
    cellStyles.base,
    selectionMode === 'multiple' && cellStyles.checkboxContainer
  ]);

  // todo: use translated text for selector
  // note cannot put focus on gridcell div since JAWS will not read the aria-rowindex
  // correctly, so unfortunately needed another div
  return (
    <div
      aria-rowindex={itemIndex + 1}
      data-oj-key={itemKey}
      class={classes}
      ref={rootRef}
      role={roles.item}
      {...(suggestion && { 'data-oj-suggestion': true })}
      {...(typeof itemKey === 'number' && { 'data-oj-key-type': 'number' })}>
      <div
        id={useId()}
        class={cellClasses}
        aria-posinset={itemIndex + 1}
        aria-setsize={-1}
        onKeyDown={handleKeyDown}
        aria-colindex={1}
        aria-selected={selectionMode !== 'none' ? isSelected : undefined}
        {...cellRoleAttributes}>
        <TabbableModeContext.Provider value={{ isTabbable }}>
          <FocusTrap isDisabled={!isTabbable} restoreFocusRef={false}>
            {children}
          </FocusTrap>
        </TabbableModeContext.Provider>
      </div>
    </div>
  );
}

/**
 * Helper method to determine whether item is selectable
 * @returns true if item is selectable (show hover effect), false otherwise
 */
const isSelectable = (selectionMode: SelectionMode, isItemSelected: boolean) => {
  return selectionMode !== 'none' && (selectionMode === 'multiple' || !isItemSelected);
};
